import React from "react";
import { Link } from "react-router-dom";

const RefundPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Refund & Cancellation Policy
          </h1>
          <p className="text-lg text-gray-600">
            Our commitment to fair and transparent refund practices
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last updated: September 2025
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          {/* Overview */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              1. Refund Policy Overview
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              At Raah Global, we strive to provide exceptional educational
              consulting services. This Refund Policy outlines the conditions
              under which refunds may be provided for our services. We believe
              in transparency and fairness in all our business dealings.
            </p>
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <p className="text-green-800 text-sm">
                <strong>Our Commitment:</strong> If any issues arise from our
                end that prevent us from delivering the promised services, we
                will provide an immediate full refund.
              </p>
            </div>
          </section>

          {/* Immediate Refund Scenarios */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              2. Immediate Full Refund Scenarios
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We will provide an <strong>immediate and complete refund</strong>{" "}
              in the following situations:
            </p>
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <h3 className="font-semibold text-green-800 mb-2">
                  ✅ Service Delivery Failures (Our Responsibility):
                </h3>
                <ul className="list-disc list-inside text-green-700 space-y-1">
                  <li>
                    Failure to submit applications within agreed deadlines due
                    to our negligence
                  </li>
                  <li>
                    Significant errors in application preparation by our team
                  </li>
                  <li>
                    Misrepresentation of university requirements or eligibility
                    criteria
                  </li>
                  <li>
                    Technical failures on our platform preventing service
                    delivery
                  </li>
                  <li>Breach of confidentiality or privacy by our staff</li>
                  <li>
                    Failure to provide promised services within the agreed
                    timeframe
                  </li>
                  <li>Any fraudulent or unethical practices by our team</li>
                </ul>
              </div>
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-2">
                  📝 Process for Immediate Refunds:
                </h3>
                <ol className="list-decimal list-inside text-blue-700 space-y-1">
                  <li>Report the issue to us immediately</li>
                  <li>We will investigate within 24-48 hours</li>
                  <li>
                    Upon confirmation, refund will be processed within 3-5
                    business days
                  </li>
                  <li>
                    Full refund amount will be returned to original payment
                    method
                  </li>
                </ol>
              </div>
            </div>
          </section>

          {/* No Refund Scenarios */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              3. No Refund Scenarios
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Refunds will <strong>NOT</strong> be provided in the following
              situations where the issue stems from the client's actions:
            </p>
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <h3 className="font-semibold text-red-800 mb-2">
                  ❌ Document and Information Issues:
                </h3>
                <ul className="list-disc list-inside text-red-700 space-y-1">
                  <li>
                    Submission of fake, forged, or altered academic documents
                  </li>
                  <li>Providing false personal or financial information</li>
                  <li>
                    Failure to provide original or certified documents when
                    requested
                  </li>
                  <li>Submitting plagiarized essays or personal statements</li>
                  <li>
                    Misrepresenting language proficiency scores or achievements
                  </li>
                  <li>Using someone else's identity or credentials</li>
                </ul>
              </div>
              <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                <h3 className="font-semibold text-orange-800 mb-2">
                  ⚠️ Non-Compliance Issues:
                </h3>
                <ul className="list-disc list-inside text-orange-700 space-y-1">
                  <li>
                    Failure to respond to our requests for information or
                    documents
                  </li>
                  <li>Missing application deadlines due to client delays</li>
                  <li>
                    Not attending scheduled meetings or consultations without
                    notice
                  </li>
                  <li>Ignoring our professional advice and recommendations</li>
                  <li>
                    Failure to pay third-party fees (university, test, visa
                    fees)
                  </li>
                  <li>Violating university or immigration policies</li>
                </ul>
              </div>
              <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                <h3 className="font-semibold text-purple-800 mb-2">
                  🚫 External Factors:
                </h3>
                <ul className="list-disc list-inside text-purple-700 space-y-1">
                  <li>
                    University rejection based on academic qualifications or
                    competition
                  </li>
                  <li>
                    Changes in university admission requirements or policies
                  </li>
                  <li>Visa application rejections by government authorities</li>
                  <li>
                    Changes in government immigration or education policies
                  </li>
                  <li>Personal changes in circumstances or career goals</li>
                  <li>Market conditions affecting scholarship availability</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Partial Refund Scenarios */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              4. Partial Refund Scenarios
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              In certain circumstances, a partial refund may be considered:
            </p>
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <h3 className="font-semibold text-yellow-800 mb-2">
                📊 Partial Refund Conditions:
              </h3>
              <ul className="list-disc list-inside text-yellow-700 space-y-2">
                <li>
                  <strong>Early Cancellation:</strong> If you cancel services
                  before we begin substantial work (within 7 days of agreement)
                </li>
                <li>
                  <strong>Mutual Agreement:</strong> Both parties agree to
                  terminate services early
                </li>
                <li>
                  <strong>Extraordinary Circumstances:</strong> Unforeseen
                  events preventing service completion (force majeure)
                </li>
                <li>
                  <strong>Service Modification:</strong> Reduction in scope of
                  services by mutual agreement
                </li>
              </ul>
              <p className="text-yellow-800 text-sm mt-3">
                <strong>Note:</strong> Partial refunds are calculated based on
                services already provided and documented work completed.
              </p>
            </div>
          </section>

          {/* Non-Refundable Fees */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              5. Non-Refundable Third-Party Fees
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The following fees are non-refundable as they are paid directly to
              third parties:
            </p>
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>
                  <strong>University Application Fees:</strong> Paid directly to
                  universities
                </li>
                <li>
                  <strong>Language Test Fees:</strong> IELTS, TOEFL, and other
                  standardized tests
                </li>
                <li>
                  <strong>Document Processing Fees:</strong> Translation,
                  notarization, and verification
                </li>
                <li>
                  <strong>Visa Application Fees:</strong> Government processing
                  fees
                </li>
                <li>
                  <strong>Courier and Shipping:</strong> Document delivery
                  services
                </li>
                <li>
                  <strong>Background Checks:</strong> Security and verification
                  services
                </li>
              </ul>
            </div>
          </section>

          {/* Refund Process */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              6. Refund Request Process
            </h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                To request a refund, please follow these steps:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-2">
                    📧 Step 1: Submit Request
                  </h3>
                  <ul className="list-disc list-inside text-blue-700 space-y-1 text-sm">
                    <li>Email us at info@raahglobalpak.kr</li>
                    <li>
                      Include your full name and service agreement details
                    </li>
                    <li>Clearly state the reason for refund request</li>
                    <li>Provide supporting documentation if applicable</li>
                  </ul>
                </div>
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <h3 className="font-semibold text-green-800 mb-2">
                    🔍 Step 2: Review Process
                  </h3>
                  <ul className="list-disc list-inside text-green-700 space-y-1 text-sm">
                    <li>We will acknowledge receipt within 24 hours</li>
                    <li>Investigation period: 3-7 business days</li>
                    <li>We may request additional information</li>
                    <li>Decision communicated in writing</li>
                  </ul>
                </div>
                <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                  <h3 className="font-semibold text-purple-800 mb-2">
                    💳 Step 3: Processing
                  </h3>
                  <ul className="list-disc list-inside text-purple-700 space-y-1 text-sm">
                    <li>
                      Approved refunds processed within 5-10 business days
                    </li>
                    <li>Refund to original payment method</li>
                    <li>Confirmation email with transaction details</li>
                    <li>Bank processing may take additional 3-5 days</li>
                  </ul>
                </div>
                <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                  <h3 className="font-semibold text-orange-800 mb-2">
                    📄 Step 4: Documentation
                  </h3>
                  <ul className="list-disc list-inside text-orange-700 space-y-1 text-sm">
                    <li>Refund receipt provided</li>
                    <li>Updated service agreement if applicable</li>
                    <li>Case closure confirmation</li>
                    <li>Feedback survey (optional)</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              7. Dispute Resolution
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you disagree with our refund decision, we offer the following
              resolution process:
            </p>
            <div className="bg-cyan-50 border border-cyan-200 p-4 rounded-lg">
              <ol className="list-decimal list-inside text-cyan-700 space-y-2">
                <li>
                  <strong>Internal Appeal:</strong> Request review by senior
                  management
                </li>
                <li>
                  <strong>Mediation:</strong> Third-party mediation if
                  disagreement persists
                </li>
                <li>
                  <strong>Arbitration:</strong> Binding arbitration as final
                  resort
                </li>
                <li>
                  <strong>Legal Action:</strong> Courts in South Korea have
                  jurisdiction
                </li>
              </ol>
            </div>
          </section>

          {/* Payment Gateway Integration */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              8. Payment Gateway and Processing
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              As we integrate payment gateways into our platform, the following
              applies:
            </p>
            <div className="space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg">
                <h3 className="font-semibold text-indigo-800 mb-2">
                  💳 Payment Processing:
                </h3>
                <ul className="list-disc list-inside text-indigo-700 space-y-1">
                  <li>
                    Secure payment processing through certified payment gateways
                  </li>
                  <li>
                    Compliance with international payment security standards
                  </li>
                  <li>
                    Multiple payment methods accepted (cards, bank transfers)
                  </li>
                  <li>Automatic receipt generation and record keeping</li>
                </ul>
              </div>
              <div className="bg-teal-50 border border-teal-200 p-4 rounded-lg">
                <h3 className="font-semibold text-teal-800 mb-2">
                  🔄 Automated Refund Processing:
                </h3>
                <ul className="list-disc list-inside text-teal-700 space-y-1">
                  <li>
                    Refunds processed through the same payment gateway used for
                    original payment
                  </li>
                  <li>
                    Faster processing times for approved refunds (2-3 business
                    days)
                  </li>
                  <li>Real-time status updates on refund progress</li>
                  <li>Automatic notification upon completion</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              9. Contact Information for Refunds
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For all refund-related inquiries, please contact us:
            </p>
            <div className="bg-cyan-50 border border-cyan-200 p-4 rounded-lg">
              <div className="space-y-2">
                <p className="text-gray-900">
                  <strong>Raah Global - Refund Department</strong>
                </p>
                <p className="text-gray-700">
                  Email: info@raahglobalpak.kr (Subject: Refund Request)
                </p>
                <p className="text-gray-700">
                  Business Registration: 815-79-00669
                </p>
                <p className="text-gray-700">
                  Company Name: 라아 글로벌 (Raah Global)
                </p>
                <p className="text-gray-700 text-sm mt-3">
                  <strong>Business Hours:</strong> Monday to Friday, 9:00 AM -
                  6:00 PM (KST)
                </p>
                <p className="text-gray-700 text-sm">
                  <strong>Response Time:</strong> Within 24 hours for refund
                  inquiries
                </p>
              </div>
            </div>
          </section>

          {/* Updates to Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              10. Updates to This Policy
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Refund Policy from time to time to reflect
              changes in our services, payment systems, or legal requirements.
              All clients will be notified of significant changes via email and
              website notification. The updated policy will apply to all new
              service agreements after the effective date.
            </p>
          </section>
        </div>

        {/* Navigation Links */}
        <div className="mt-8 text-center space-x-4">
          <Link
            to="/privacy-policy"
            className="text-cyan-600 hover:text-cyan-700 font-medium"
          >
            Privacy Policy
          </Link>
          <span className="text-gray-400">|</span>
          <Link
            to="/terms-of-service"
            className="text-cyan-600 hover:text-cyan-700 font-medium"
          >
            Terms of Service
          </Link>
          <span className="text-gray-400">|</span>
          <Link
            to="/"
            className="text-cyan-600 hover:text-cyan-700 font-medium"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
